import React, { useEffect } from "react";

import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { Button } from "primereact/button";

import classes from "./SetInventory.module.scss";
import { Dialog } from "primereact/dialog";
import { classNames as cx } from "primereact/utils";
import { InputText } from "primereact/inputtext";
import { IInventory } from "../../Inventory.model";
import { LabelValue } from "../../../../../components/LabelValue/LabelValue";

type SetInventoryProps = {
  item: IInventory | null;
  onHide: () => void;
  onAdd: (inventory: IInventory) => void;
  onUpdate: (inventory: IInventory) => void;
  requestPending: boolean;
};

export const SetInventory = ({
  item,
  onAdd,
  onUpdate,
  onHide,
  requestPending,
}: SetInventoryProps) => {
  const formSchema = Yup.object().shape({
    name: Yup.string().required("Field is required"),
    number: Yup.string().required("Field is required"),
  });

  const form = useForm<IInventory>({
    resolver: yupResolver(formSchema),
    shouldFocusError: false,
    defaultValues: {
      name: "",
      number: "",
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = form;

  useEffect(() => {
    if (item?.id) {
      reset(item);
    }
    return () => reset();
  }, [item, reset]);

  const handleSave = (formData: IInventory) => {
    if (item?.id) {
      onUpdate(formData);
      return;
    }
    onAdd(formData);
  };

  const productDialogFooter = (
    <React.Fragment>
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text p-button-secondary"
        onClick={onHide}
      />
      <Button
        label="Save"
        icon="pi pi-check"
        className="p-button-text p-button-primary"
        onClick={handleSubmit(handleSave)}
        disabled={requestPending}
      />
    </React.Fragment>
  );

  return (
    <Dialog
      visible={true}
      style={{ width: "450px" }}
      header="Details"
      modal
      className="p-fluid"
      footer={productDialogFooter}
      onHide={onHide}
      draggable={false}
    >
      <div className={classes.field}>
        <LabelValue label="Inventory name" isRequired />
        <Controller
          name="name"
          render={({ field }) => (
            <InputText
              {...field}
              placeholder="Inventory name"
              className={cx({
                "p-invalid": errors.name,
              })}
            />
          )}
          control={control}
        />
        {errors.name && (
          <small className="p-error">{errors.name?.message}</small>
        )}
      </div>
      <div className={classes.field}>
        <LabelValue label="Inventory number" isRequired />
        <Controller
          name="number"
          render={({ field }) => (
            <InputText
              {...field}
              placeholder="Inventory number"
              className={cx({
                "p-invalid": errors.number,
              })}
            />
          )}
          control={control}
        />
        {errors.name && (
          <small className="p-error">{errors.number?.message}</small>
        )}
      </div>
    </Dialog>
  );
};
